import React, { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Row,
  Stack,
  Carousel,
} from "react-bootstrap";

/**
 * DUIInsuranceLanding_v05_Carousel
 * ----------------------------------------------------------------------------
 * Date (America/Denver): Wednesday, August 27, 2025
 * Chat URL: PASTE_THE_URL_OF_THIS_CHAT_HERE
 *
 * Summary
 * - Hero background uses react-bootstrap Carousel (full-bleed behind content).
 * - Each slide carries its own title/subline. The left-hand hero copy
 *   synchronizes with the active slide; each slide also has a small caption.
 * - Quote form is aligned to the right on desktop, in a shadowed Card.
 *
 * Functional Features
 * - "Quick DUI Quote" form with:
 *   - Contact: first/last name, email, phone (required), preferred contact
 *   - Location: ZIP, state
 *   - DUI info: count, most recent date, license status, filing (SR-22/FR-44)
 *   - Vehicle (optional): Own a vehicle?, Y/M/M, usage notes
 *   - Coverage preference (optional), notes
 * - Simple client-side validation + TEST_MODE console logging.
 * - Payload includes meta fields (version, timestamp, userAgent).
 */

const CLIENT_VERSION = "v05" as const;
const TEST_MODE = false;
const API_URL = "https://your.api.example/dui-quote"; // replace with your endpoint

// Optional: put your images under this folder; filenames can be adjusted below.
const imagePath = "insurance/images/DUI-Insurance/";

// ---------- Types
type ContactPref = "phone" | "email" | "text";
type LicenseStatus = "valid" | "suspended" | "restricted";
type FilingType = "none" | "SR-22" | "FR-44";

interface HeroSlide {
  src: string;
  alt: string;
  title: string;
  subline: string;
  smallCaption?: string;
}

interface LandingContent {
  brandName: string;
  navLinks: { label: string; href: string }[];
  headlineFallback: { title: string; subline: string };
  slides: HeroSlide[];
  features: string[];
  benefits: string[];
  disclaimers: string[];
  footer: {
    company: string;
    links: { label: string; href: string }[];
    copyright: string;
  };
}

interface LeadPayload {
  version: typeof CLIENT_VERSION;
  tsISO: string;
  userAgent: string;

  // Contact
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  preferredContact: ContactPref;

  // Location
  zip: string;
  state: string;

  // DUI specifics
  duiCount: number | "";
  duiMostRecentDate: string; // yyyy-mm-dd
  licenseStatus: LicenseStatus;
  filing: FilingType;
  filingState: string;

  // Vehicle (optional)
  ownsVehicle: boolean;
  vehicleYear?: string;
  vehicleMake?: string;
  vehicleModel?: string;

  // Coverage
  coveragePreference?: "liability" | "full" | "";

  // Misc
  notes?: string;
  testMode?: boolean;
}

// ---------- CONTENT OBJECT (as requested)
export const DUIInsuranceLanding_v05_Carousel_CONTENT: LandingContent = {
  brandName: "Easy Button Insurance — DUI",
  navLinks: [
    { label: "Get a Quote", href: "#quote" },
    { label: "How It Works", href: "#how" },
    { label: "FAQs", href: "#faqs" },
    { label: "Contact", href: "#contact" },
  ],
  headlineFallback: {
    title: "DUI Insurance Made Simple",
    subline: "SR-22/FR-44 filings, fast quotes, and flexible options.",
  },
  slides: [
    {
      src: imagePath + "AdobeStock_1463253262_Preview-FLIP.jpg",
      alt: "Night street with car lights symbolizing a fresh start",
      title: "Need SR-22 or FR-44?",
      subline: "We’ll file the right proof of financial responsibility for your state.",
      smallCaption: "Compliance without the headache.",
    },

    {
      src: imagePath + "AdobeStock_1360258027_Preview-FLIP.jpg",
      alt: "Car keys over auto policy",
      title: "Quote in Minutes",
      subline: "Submit once. We shop multiple carriers so you don’t have to.",
      smallCaption: "Fast, competitive options.",
    },
    {
      src: imagePath + "AdobeStock_370848671_Preview.jpeg",
      alt: "Daytime driving, open road ahead",
      title: "Get Back on the Road",
      subline: "Coverage built for your situation and budget.",
      smallCaption: "Flexible plans. Friendly experts.",
    },
  ],
  features: [
    "SR-22 & FR-44 filing assistance",
    "Multi-carrier shopping",
    "State-specific compliance guidance",
    "Flexible down payments",
    "Monthly pay options",
    "Text/Email friendly service",
  ],
  benefits: [
    "Faster license reinstatement & DMV compliance",
    "Save time—one short form; we shop for you",
    "Reduce surprises with transparent coverage",
    "Affordable options that fit your budget",
    "Keep moving—same-day proof often available",
  ],
  disclaimers: [
    "Coverage and eligibility vary by carrier and state.",
    "Submitting a request does not bind coverage.",
    "Final rate depends on underwriting and verification.",
  ],
  footer: {
    company: "Easy Button Insurance",
    links: [
      { label: "Privacy", href: "#privacy" },
      { label: "Terms", href: "#terms" },
      { label: "Licensing", href: "#licensing" },
    ],
    copyright: `© ${new Date().getFullYear()} Easy Button Insurance. All rights reserved.`,
  },
};

// ---------- Component
type Props = {
  content?: LandingContent;
};

export default function DUIInsuranceLandingPage_v05({ content }: Props) {
  const {
    brandName,
    navLinks,
    headlineFallback,
    slides,
    features,
    benefits,
    disclaimers,
    footer,
  } = content ?? DUIInsuranceLanding_v05_Carousel_CONTENT;

  // Carousel active index for synchronized hero copy
  const [active, setActive] = useState(0);
  const activeSlide = slides[active] ?? {
    title: headlineFallback.title,
    subline: headlineFallback.subline,
  };

  // ---- Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [preferredContact, setPreferredContact] = useState<ContactPref>("phone");

  const [zip, setZip] = useState("");
  const [stateUS, setStateUS] = useState("");

  const [duiCount, setDuiCount] = useState<number | "">("");
  const [duiMostRecentDate, setDuiMostRecentDate] = useState("");
  const [licenseStatus, setLicenseStatus] = useState<LicenseStatus>("valid");
  const [filing, setFiling] = useState<FilingType>("none");
  const [filingState, setFilingState] = useState("");

  const [ownsVehicle, setOwnsVehicle] = useState(true);
  const [vehicleYear, setVehicleYear] = useState("");
  const [vehicleMake, setVehicleMake] = useState("");
  const [vehicleModel, setVehicleModel] = useState("");

  const [coveragePreference, setCoveragePreference] = useState<"" | "liability" | "full">("");
  const [notes, setNotes] = useState("");

  // UI
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const payload: LeadPayload = useMemo(
    () => ({
      version: CLIENT_VERSION,
      tsISO: new Date().toISOString(),
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "server",

      firstName,
      lastName,
      email,
      phone,
      preferredContact,

      zip,
      state: stateUS,

      duiCount,
      duiMostRecentDate,
      licenseStatus,
      filing,
      filingState,

      ownsVehicle,
      vehicleYear: ownsVehicle ? vehicleYear : undefined,
      vehicleMake: ownsVehicle ? vehicleMake : undefined,
      vehicleModel: ownsVehicle ? vehicleModel : undefined,

      coveragePreference,
      notes,
      testMode: TEST_MODE,
    }),
    [
      firstName,
      lastName,
      email,
      phone,
      preferredContact,
      zip,
      stateUS,
      duiCount,
      duiMostRecentDate,
      licenseStatus,
      filing,
      filingState,
      ownsVehicle,
      vehicleYear,
      vehicleMake,
      vehicleModel,
      coveragePreference,
      notes,
    ]
  );

  // Simple validation
  function validate(): string | null {
    if (!firstName.trim()) return "Please enter your first name.";
    if (!lastName.trim()) return "Please enter your last name.";
    if (!/^\S+@\S+\.\S+$/.test(email)) return "Please enter a valid email.";
    if (!/^[0-9\-\+\(\)\s]{7,}$/.test(phone)) return "Please enter a valid phone.";
    if (!zip.trim()) return "Please enter your ZIP.";
    if (!stateUS.trim()) return "Please select your state.";
    if (duiCount === "") return "Please enter your DUI count (0 if none).";
    if (Number(duiCount) > 0 && !duiMostRecentDate)
      return "Please provide your most recent DUI date.";
    if ((filing === "SR-22" || filing === "FR-44") && !filingState)
      return "Please select a filing state.";
    return null;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setOk(null);

    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    if (TEST_MODE) {
      console.log("DUI QUOTE PAYLOAD", payload);
      setOk("Test mode: payload logged to console.");
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      setOk("Thanks! We received your request and will reach out shortly.");
      // Optionally reset minimal fields
    } catch (err: any) {
      setError(err?.message ?? "Something went wrong submitting your request.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {/* NAV */}
      <Navbar bg="light" expand="lg" className="shadow-sm">
        <Container>
          <Navbar.Brand href="#home">{brandName}</Navbar.Brand>
          <Navbar.Toggle aria-controls="main-nav" />
          <Navbar.Collapse id="main-nav">
            <Nav className="ms-auto">
              {navLinks.map((n) => (
                <Nav.Link key={n.href} href={n.href}>
                  {n.label}
                </Nav.Link>
              ))}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO SECTION (Carousel behind content) */}
      <section id="home" className="position-relative overflow-hidden" style={{ minHeight: "74vh", border:'dashed orange 5px' }}>
        {/* Background Carousel */}
        <div className="position-absolute top-0 start-0 w-100 h-100" style={{ zIndex: 0}}>
          <Carousel
            fade
            controls
            indicators
            interval={6000}
            onSelect={(i) => setActive(i)}
            className="w-100 h-100"
          >
            
            {slides.map((s, i) => (
              <Carousel.Item key={i} className="w-100 h-100">
                
                <img
                  src={s.src}
                  alt={s.alt}
                  className="w-100"
                  style={{
                    height: "74vh",
                    objectFit: "cover",
                    filter: "brightness(0.75)",
                  }}
                />
                {s.smallCaption && (
                  <Carousel.Caption className="text-start">
                    <small className="bg-dark bg-opacity-50 px-2 py-1 rounded-1">
                      {s.smallCaption}
                    </small>
                  </Carousel.Caption>
                )}
              </Carousel.Item>
            ))}
          </Carousel>
        </div>

        {/* Foreground Content */}
        {/* <Container className="position-relative" style={{ zIndex: 1, border:'dashed red 4px' }}> */}
        {/* REPLACED ORIGINAL TO MAKE MORE ROOM FOR THE FORM. */}
        <Container
                    fluid
                    className="position-relative px-3 px-md-4"
                    style={{ zIndex: 1, border: "4px dashed red", maxWidth: "1600px", marginInline: "auto" }}
                    >
          <Row className="align-items-center py-5" style={{ minHeight: "74vh" }}>
            <Col md={6} className="text-white">
              <div className="d-none d-md-block">
                <h1 className="display-5 fw-bold">{activeSlide.title}</h1>
                <p className="lead">{activeSlide.subline}</p>
                <Stack direction="horizontal" gap={2} className="flex-wrap mt-3">
                  {features.slice(0, 4).map((f, idx) => (
                    <Badge bg="light" text="dark" key={idx}>
                      {f}
                    </Badge>
                  ))}
                </Stack>
              </div>
              {/* On small screens, show a subtle header above the form */}
              <div className="d-md-none mb-3">
                <h2 className="h3 text-light">{headlineFallback.title}</h2>
                <p className="text-light-50 m-0">{headlineFallback.subline}</p>
              </div>
            </Col>

            <Col
              id="quote"
              md={{ span: 5, offset: 1 }}
              lg={{ span: 4, offset: 2 }}
              className="mt-3 mt-md-0" style={{border: 'green dashed 5px'}}
            >
              <Card className="shadow-lg">
                <Card.Body>
                  <h3 className="h4 mb-1">Quick DUI Quote</h3>
                  <div className="text-muted mb-3">
                    Same-day filings available in many states.
                  </div>

                  {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                      {error}
                    </Alert>
                  )}
                  {ok && (
                    <Alert variant="success" onClose={() => setOk(null)} dismissible>
                      {ok}
                    </Alert>
                  )}

                  <Form onSubmit={onSubmit}>
                    {/* Contact */}
                    <Row className="g-2">
                      <Col md={6}>
                        <Form.Group controlId="firstName">
                          <Form.Label>First Name</Form.Label>
                          <Form.Control
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="lastName">
                          <Form.Label>Last Name</Form.Label>
                          <Form.Control
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="g-2 mt-1">
                      <Col md={6}>
                        <Form.Group controlId="email">
                          <Form.Label>Email</Form.Label>
                          <Form.Control
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="phone">
                          <Form.Label>Phone</Form.Label>
                          <Form.Control
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mt-2" controlId="preferredContact">
                      <Form.Label>Preferred Contact</Form.Label>
                      <Form.Select
                        value={preferredContact}
                        onChange={(e) => setPreferredContact(e.target.value as ContactPref)}
                      >
                        <option value="phone">Phone</option>
                        <option value="email">Email</option>
                        <option value="text">Text</option>
                      </Form.Select>
                    </Form.Group>

                    {/* Location */}
                    <Row className="g-2 mt-3">
                      <Col md={6}>
                        <Form.Group controlId="zip">
                          <Form.Label>ZIP</Form.Label>
                          <Form.Control value={zip} onChange={(e) => setZip(e.target.value)} />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="state">
                          <Form.Label>State</Form.Label>
                          <Form.Control
                            placeholder="CO, FL, VA…"
                            value={stateUS}
                            onChange={(e) => setStateUS(e.target.value.toUpperCase())}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* DUI */}
                    <Row className="g-2 mt-3">
                      <Col md={6}>
                        <Form.Group controlId="duiCount">
                          <Form.Label>DUI Count</Form.Label>
                          <Form.Control
                            inputMode="numeric"
                            value={duiCount}
                            onChange={(e) => {
                              const v = e.target.value;
                              setDuiCount(v === "" ? "" : Math.max(0, Number(v)));
                            }}
                            placeholder="0, 1, 2…"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="duiMostRecentDate">
                          <Form.Label>Most Recent DUI Date</Form.Label>
                          <Form.Control
                            type="date"
                            value={duiMostRecentDate}
                            onChange={(e) => setDuiMostRecentDate(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="g-2 mt-1">
                      <Col md={6}>
                        <Form.Group controlId="licenseStatus">
                          <Form.Label>License Status</Form.Label>
                          <Form.Select
                            value={licenseStatus}
                            onChange={(e) =>
                              setLicenseStatus(e.target.value as LicenseStatus)
                            }
                          >
                            <option value="valid">Valid</option>
                            <option value="suspended">Suspended</option>
                            <option value="restricted">Restricted</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="filing">
                          <Form.Label>Filing Needed</Form.Label>
                          <Form.Select
                            value={filing}
                            onChange={(e) => setFiling(e.target.value as FilingType)}
                          >
                            <option value="none">None / Not Sure</option>
                            <option value="SR-22">SR-22</option>
                            <option value="FR-44">FR-44</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mt-2" controlId="filingState">
                      <Form.Label>Filing State</Form.Label>
                      <Form.Control
                        placeholder="If SR-22/FR-44, enter state"
                        value={filingState}
                        onChange={(e) => setFilingState(e.target.value.toUpperCase())}
                      />
                    </Form.Group>

                    {/* Vehicle */}
                    <Form.Group className="mt-3" controlId="ownsVehicle">
                      <Form.Check
                        type="switch"
                        label="I own a vehicle"
                        checked={ownsVehicle}
                        onChange={(e) => setOwnsVehicle(e.currentTarget.checked)}
                      />
                    </Form.Group>
                    {ownsVehicle && (
                      <Row className="g-2 mt-1">
                        <Col md={4}>
                          <Form.Group controlId="vehicleYear">
                            <Form.Label>Year</Form.Label>
                            <Form.Control
                              value={vehicleYear}
                              onChange={(e) => setVehicleYear(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group controlId="vehicleMake">
                            <Form.Label>Make</Form.Label>
                            <Form.Control
                              value={vehicleMake}
                              onChange={(e) => setVehicleMake(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group controlId="vehicleModel">
                            <Form.Label>Model</Form.Label>
                            <Form.Control
                              value={vehicleModel}
                              onChange={(e) => setVehicleModel(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                    {/* Coverage */}
                    <Form.Group className="mt-3" controlId="coveragePreference">
                      <Form.Label>Coverage Preference (optional)</Form.Label>
                      <Form.Select
                        value={coveragePreference}
                        onChange={(e) =>
                          setCoveragePreference(e.target.value as "" | "liability" | "full")
                        }
                      >
                        <option value="">I’m not sure yet</option>
                        <option value="liability">Liability Only</option>
                        <option value="full">Full Coverage</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mt-2" controlId="notes">
                      <Form.Label>Notes (optional)</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder="Anything else we should know?"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                      />
                    </Form.Group>

                    <Button
                      type="submit"
                      className="mt-3 w-100"
                      disabled={submitting}
                      variant="primary"
                    >
                      {submitting ? "Submitting…" : "Get My DUI Quote"}
                    </Button>

                    <div className="mt-2 text-muted" style={{ fontSize: "0.85rem" }}>
                      By submitting, you agree to be contacted about your quote. Standard
                      messaging rates may apply.
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* HOW IT WORKS / BENEFITS */}
      <section id="how" className="py-5 bg-light">
        <Container>
          <Row className="g-4">
            <Col md={6}>
              <h2 className="h4">Why choose us for DUI insurance?</h2>
              <ul className="mt-3">
                {benefits.map((b, i) => (
                  <li key={i} className="mb-2">
                    {b}
                  </li>
                ))}
              </ul>
            </Col>
            <Col md={6}>
              <h2 className="h4">What you get</h2>
              <Stack direction="horizontal" gap={2} className="flex-wrap mt-3">
                {features.map((f, i) => (
                  <Badge bg="secondary" key={i} className="mb-2">
                    {f}
                  </Badge>
                ))}
              </Stack>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQS (placeholder) */}
      <section id="faqs" className="py-5">
        <Container>
          <h2 className="h4 mb-3">FAQs</h2>
          <p className="text-muted m-0">
            Have questions about SR-22 or FR-44, timelines, or state rules? Start a
            quote and our team will guide you step-by-step.
          </p>
        </Container>
      </section>

      {/* DISCLAIMERS */}
      <section className="py-4 bg-light">
        <Container>
          <small className="text-muted">
            {disclaimers.map((d, i) => (
              <span key={i}>
                {i > 0 ? " · " : ""}
                {d}
              </span>
            ))}
          </small>
        </Container>
      </section>

      {/* FOOTER */}
      <footer id="contact" className="py-4 border-top">
        <Container>
          <Row className="align-items-center">
            <Col md={6}>
              <strong>{footer.company}</strong>
              <div className="text-muted">{footer.copyright}</div>
            </Col>
            <Col md={6} className="mt-2 mt-md-0">
              <Stack direction="horizontal" gap={3} className="justify-content-md-end">
                {footer.links.map((l) => (
                  <a key={l.href} href={l.href} className="text-decoration-none">
                    {l.label}
                  </a>
                ))}
              </Stack>
            </Col>
          </Row>
        </Container>
      </footer>
    </>
  );
}
